import React, { createContext, useContext } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import type { PaletteMode } from '@mui/material';
import { BRAND_COLORS, BRAND_GRADIENT, BRAND_GRADIENT_DEEP, brandRgba } from '../config/brandColors';

interface ThemeContextType {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const mode: PaletteMode = 'light';

  const toggleColorMode = () => {
    // Tema sempre claro
  };

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: BRAND_COLORS.main,
            light: BRAND_COLORS.light,
            dark: BRAND_COLORS.dark,
            contrastText: '#ffffff',
          },
          secondary: {
            main: BRAND_COLORS.cyan,
            light: '#67e8f9',
            dark: BRAND_COLORS.dark,
            contrastText: '#ffffff',
          },
          success: {
            main: '#10b981',
            light: '#34d399',
            dark: '#059669',
          },
          warning: {
            main: '#f59e0b',
            light: '#fbbf24',
            dark: '#d97706',
          },
          error: {
            main: '#ef4444',
            light: '#f87171',
            dark: '#dc2626',
          },
          info: {
            main: BRAND_COLORS.main,
            light: BRAND_COLORS.light,
            dark: BRAND_COLORS.dark,
          },
          background: {
            default: BRAND_COLORS.bg,
            paper: '#ffffff',
          },
          text: {
            primary: '#475569',
            secondary: '#64748b',
            disabled: '#94a3b8',
          },
          divider: 'rgba(148, 163, 184, 0.2)',
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: '#ffffff',
                color: '#1e293b',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              },
            },
          },
          // O chat de equipe fica num portal fora do modal: com o focus trap ligado,
          // qualquer diálogo aberto impede digitar no chat.
          MuiModal: {
            defaultProps: { disableEnforceFocus: true },
          },
          MuiDialog: {
            defaultProps: { disableEnforceFocus: true },
          },
          MuiDrawer: {
            defaultProps: { ModalProps: { disableEnforceFocus: true } },
            styleOverrides: {
              paper: {
                backgroundColor: '#ffffff',
                borderRight: '1px solid rgba(148, 163, 184, 0.2)',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: '#ffffff',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  borderColor: 'rgba(148, 163, 184, 0.3)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.15)',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: '#ffffff',
                border: '1px solid rgba(148, 163, 184, 0.2)',
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: 'rgba(148, 163, 184, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(148, 163, 184, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: BRAND_COLORS.main,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748b',
                  '&.Mui-focused': {
                    color: BRAND_COLORS.main,
                  },
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: '0.5rem',
                fontWeight: 500,
                padding: '0.625rem 1.25rem',
              },
              contained: {
                background: BRAND_GRADIENT,
                boxShadow: `0 4px 14px 0 ${brandRgba(0.3)}`,
                '&:hover': {
                  background: BRAND_GRADIENT_DEEP,
                  boxShadow: `0 6px 20px 0 ${brandRgba(0.4)}`,
                },
              },
              outlined: {
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#1e293b',
                '&:hover': {
                  borderColor: BRAND_COLORS.main,
                  backgroundColor: brandRgba(0.1),
                },
              },
            },
          },
          MuiListItem: {
            styleOverrides: {
              root: {
                borderRadius: '0.5rem',
                margin: '0.25rem 0.5rem',
                '&:hover': {
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: brandRgba(0.15),
                  borderLeft: `3px solid ${BRAND_COLORS.main}`,
                  '&:hover': {
                    backgroundColor: brandRgba(0.2),
                  },
                  '& .MuiListItemIcon-root': {
                    color: BRAND_COLORS.main,
                  },
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                backgroundColor: 'rgba(148, 163, 184, 0.1)',
                color: '#1e293b',
                border: '1px solid rgba(148, 163, 184, 0.2)',
                '&.MuiChip-colorPrimary': {
                  backgroundColor: brandRgba(0.15),
                  color: BRAND_COLORS.main,
                  borderColor: BRAND_COLORS.main,
                },
              },
            },
          },
          MuiTooltip: {
            defaultProps: {
              // Evita que o balão do tooltip (comum em fileiras de IconButton) capture
              // o mouse e bloqueie o clique no botão ao lado.
              disableInteractive: true,
              enterDelay: 450,
              PopperProps: {
                sx: { pointerEvents: 'none' },
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                color: '#64748b',
                '&:hover': {
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                  color: '#1e293b',
                },
              },
            },
          },
        },
        typography: {
          fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: { fontWeight: 700 },
          h2: { fontWeight: 600 },
          h3: { fontWeight: 600 },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
          button: { fontWeight: 500 },
        },
        shape: {
          borderRadius: 8,
        },
      }),
    []
  );

  const value = {
    mode,
    toggleColorMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
